import { BearingEnvelope, TrussEnvelope } from '@paragon/contract-library';
import { Direction2D, LineSegment2D, Point2D, point2DEquals } from '@paragon/geometry-library';

/**
 * Creates a `LineSegment2D` from the left and right points of the envelope. If the points are equal, returns `null`.
 *
 * It is safer to use this function than to create a `LineSegment2D` directly from the points, because envelopes can
 *   sometimes have the same left and right points during editing.
 */
export function getEnvelopeSegment(
  envelope: Pick<TrussEnvelope | BearingEnvelope, 'leftPoint' | 'rightPoint'>,
): LineSegment2D | null {
  if (point2DEquals(envelope.leftPoint, envelope.rightPoint)) return null;
  return LineSegment2D.fromPointObjects(envelope.leftPoint, envelope.rightPoint);
}

/**
 * Creates a `LineSegment2D` from the left and right overhang points of the envelope. If the envelope's points are
 * equal, returns `null`.
 *
 * It is safer to use this function than to create a `LineSegment2D` directly from the points, because envelopes can
 * sometimes have the same left and right points during editing.
 */
export function getEnvelopeSegmentWithOverhangs(envelope: TrussEnvelope): LineSegment2D | null {
  if (point2DEquals(envelope.leftPoint, envelope.rightPoint)) return null;
  const envelopeRight = Direction2D.fromPointObjects(envelope.leftPoint, envelope.rightPoint);
  const leftOverhangPoint = Point2D.fromObject(envelope.leftPoint).translate(
    envelopeRight.toVector(-envelope.leftOverhang),
  );
  const rightOverhangPoint = Point2D.fromObject(envelope.rightPoint).translate(
    envelopeRight.toVector(envelope.rightOverhang),
  );
  return new LineSegment2D(leftOverhangPoint, rightOverhangPoint);
}
